import dayjs from 'dayjs';
import DayJSCustomParseFormat from 'dayjs/plugin/customParseFormat';
import DayJSUtc from 'dayjs/plugin/utc';
import DayJSTimezone from 'dayjs/plugin/timezone';
import DayJSAdvancedFormat from 'dayjs/plugin/advancedFormat';
import IsBetween from 'dayjs/plugin/isBetween';
import IsTomorrow from 'dayjs/plugin/isTomorrow';
import IsToday from 'dayjs/plugin/isToday';
import IsYesterday from 'dayjs/plugin/isYesterday';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import moment from 'moment-timezone';
import type { UtcTimezoneInfo, WeekOfMonth } from '@/types/common';
import type { Timezone } from '@/types/country-timezone';

dayjs.extend(DayJSCustomParseFormat);
dayjs.extend(DayJSUtc);
dayjs.extend(DayJSTimezone);
dayjs.extend(DayJSAdvancedFormat);
dayjs.extend(IsBetween);
dayjs.extend(IsTomorrow);
dayjs.extend(IsToday);
dayjs.extend(IsYesterday);
dayjs.extend(isSameOrAfter);

export const isFutureTime = (firstDateTime: string | Date, secondDateTime: string | Date, time: string, timezone: string): boolean => {
	// Get the current time
	const currentTime = dayjs(firstDateTime).tz(timezone).format(DATE_TIME_ISO8601_FORMAT);
	const currentDateTimeObj = dayjs(currentTime);

	// Parse the date and time values
	const dateObj = dayjs(secondDateTime, DATE_TIME_ISO8601_FORMAT);
	const timeObj = dayjs(time, TIME_12_HOUR_2_DIGITS_FORMAT);

	// Combine them by setting the time of the date
	const futureTime = dateObj
		.set('hour', timeObj.get('hour'))
		.set('minute', timeObj.get('minute'));
	const futureTimeUtc = futureTime.format(DATE_TIME_ISO8601_FORMAT);
	return currentDateTimeObj.isBefore(futureTimeUtc);
};

/**
 * Convert from dateTime (UTC) to time format
 * Ex: Given format 'h:mm A'
 * Result: '2023-05-19T15:30:00Z' --> '03:30 PM'
 */
export const convertTimeFromUtc = (dateTimeUTC: string) => {
	if (!dateTimeUTC) {
		return '';
	}

	return dayjs(dateTimeUTC, DATE_TIME_ISO8601_FORMAT, true).format(TIME_12_HOUR_2_DIGITS_FORMAT);
};

/**
 * Convert dateTime with target timezone to dateTime(UTC)
 * Ex:
 * Timezone: New York
 * DateTime with timezone: 2023-12-21T05:30:00Z
 * Result: '2023-12-21T05:30:00Z' --> '2023-12-21T10:30:00Z'
 */
export const convertDateTimeToUtc = (dateTime: string | Date, timezone?: string, format = DATE_TIME_ISO8601_FORMAT) => {
	if (!dateTime) {
		return '';
	}

	const targetTimezone = timezone || dayjs.tz.guess();
	return dayjs.tz(dateTime, targetTimezone).utc().format(format);
};

/**
 * Convert dateTime to display format without timezone conversion
 * Ex: Given format 'YYYY-MM-DD HH:mm'
 * Result: '2023-12-21T13:30:00Z' --> '21-Dec-2023 13:30'
 */
export const convertDateTimeWithStrictTimezone = (dateTime: string | Date, timezone: string, format = DISPLAY_DATE_FORMAT) => {
	if (!dateTime || !timezone) {
		return '';
	}
	return dayjs.tz(dateTime, timezone).format(format);
};

/**
 * Convert date and time to dateTime UTC with or without timezone
 * Ex: If timezone not passing
 * Date: '2023-12-21'
 * Time '03:30 PM'
 * Result: 2023-12-21T15:30:00Z
 *
 * Ex: If timezone passing
 * Date: '2023-12-21'
 * Time '03:30 PM'
 * Timezone: New York
 * Result: 2023-12-21T10:30:00Z
 */
export const convertDateAndTimeToUtc = (date: string, time: string, timezone?: string) => {
	if (!date && !time) {
		return '';
	}
	const dateObj = dayjs(date, DATE_TIME_ISO8601_FORMAT, true);
	const timeObj = dayjs(time || '12:00 AM', TIME_12_HOUR_2_DIGITS_FORMAT, true);
	const dateTime = dateObj.hour(timeObj.hour()).minute(timeObj.minute());
	const strDateTime = dateTime.format(DATE_TIME_ISO8601_FORMAT);

	if (timezone) {
		return convertDateTimeToUtc(strDateTime, timezone);
	}
	return strDateTime;
};

/**
 * Convert dateTime (UTC) to display dateTime format with target timezone
 * Ex:
 * DateTime UTC: '2023-12-21T21:00:00Z'
 * Display Format: 'YYYY-MM-DD h:mm A'
 * Timezone: New York
 * Result: '2023-12-21 04:00 PM'
 */
export const convertDateTimeFromUtcToTimezone = (dateTime: string | Date, timezone: string, format = DISPLAY_DATE_FORMAT) => {
	if (!dateTime) {
		return '';
	}
	return dayjs(dateTime).tz(timezone).format(format);
};

export const getTimezoneAbbreviation = (timezone: string) => {
	const tz = moment.tz(timezone).format('z');
	if (tz.includes('+')) {
		// Return GMT format for timezone that's not support abbreviation e.g. GMT+7
		return dayjs.tz(new Date(), timezone).format('z');
	}

	// Return timezone abbreviation
	return moment.tz(timezone).format('z');
};

export const calculateNumberOfDays = (startDate: string, endDate: string) => {
	// Parse the start and end date strings into Day.js objects
	const startDateObj = dayjs(startDate);
	const endDateObj = dayjs(endDate);

	// Calculate the difference in days
	return endDateObj.diff(startDateObj, 'days');
};

export const roundToNearestHour = (date: Date): Date => {
	const cloneDateObject = new Date(date);
	cloneDateObject.setMinutes(cloneDateObject.getMinutes() + 30);
	cloneDateObject.setMinutes(0, 0, 0);
	if (cloneDateObject < date) {
		// Add 30 min if date was round down
		cloneDateObject.setMinutes(cloneDateObject.getMinutes() + 30);
	}
	return cloneDateObject;
};

export const isBetween = (targetDateTime: string | Date, startDateTime: string | Date, endDateTime: string | Date): boolean => {
	return dayjs(targetDateTime).isBetween(startDateTime, endDateTime);
};

export const isToday = (dateTime: string | Date, timezone: string): boolean => {
	if (!dateTime) {
		return false;
	}
	return dayjs.tz(dateTime, timezone).isToday();
};

export const isTomorrow = (dateTime: string | Date, timezone: string): boolean => {
	if (!dateTime) {
		return false;
	}
	return dayjs.tz(dateTime, timezone).isTomorrow();
};

export const isYesterday = (dateTime: string | Date, timezone: string): boolean => {
	if (!dateTime) {
		return false;
	}
	return dayjs.tz(dateTime, timezone).isYesterday();
};

export const convertToDisplayStartAndEndDateTime = (startDateTime: string, endDateTime: string, timezone: string) => {
	if (!startDateTime || !endDateTime || !timezone) {
		return '';
	}

	const isSameDay = dayjs(startDateTime).isSame(endDateTime, 'date');
	const timezoneAbbr = getTimezoneAbbreviation(timezone);
	let dateTime = '';
	if (isSameDay) {
		const formattedDate = convertDateTimeFromUtcToTimezone(startDateTime, timezone);
		const formattedStartTime = convertDateTimeFromUtcToTimezone(startDateTime, timezone, TIME_12_HOUR_FORMAT);
		const formattedEndTime = convertDateTimeFromUtcToTimezone(endDateTime, timezone, TIME_12_HOUR_FORMAT);
		dateTime = `${formattedDate}\n${formattedStartTime} - ${formattedEndTime}`;
	} else {
		const formattedStartDate = convertDateTimeFromUtcToTimezone(startDateTime, timezone);
		const formattedStartTime = convertDateTimeFromUtcToTimezone(startDateTime, timezone, TIME_12_HOUR_FORMAT);
		const formattedEndDateTime = convertDateTimeFromUtcToTimezone(endDateTime, timezone, `${DISPLAY_DATE_FORMAT} ${TIME_12_HOUR_FORMAT}`);
		dateTime = `${formattedStartDate}\n${formattedStartTime} - ${formattedEndDateTime}`;
	}

	return `${dateTime} ${timezoneAbbr}`;
};

export const calculateHappeningNow = (start: UtcTimezoneInfo, end: UtcTimezoneInfo) => {
	const currentDateTime = convertDateTimeFromUtcToTimezone(new Date(), start.timezone, DATE_TIME_ISO8601_FORMAT);
	const startDateTime = convertDateTimeFromUtcToTimezone(start.utc, start.timezone, DATE_TIME_ISO8601_FORMAT);
	const endDateTime = convertDateTimeFromUtcToTimezone(end.utc, end.timezone, DATE_TIME_ISO8601_FORMAT);
	const isHappeningNow = isBetween(currentDateTime, startDateTime, endDateTime);

	return isHappeningNow;
};

export const convertDisplaySelectedTimezone = (timezone: Timezone) => {
	const timezoneName = timezone.name;
	const timezoneOffset = timezone.timezoneOffset ? `(GMT${timezone.timezoneOffset})` : '';
	return `${timezoneName} ${timezoneOffset}`;
};

export const getCurrentUtcDateTimeInSpecificTimezone = (timezone: string) => {
	// Get current time in specific timezone
	const currentDateTime = dayjs().tz(timezone).format(DATE_TIME_ISO8601_FORMAT);
	// Convert the current time in that timezone to UTC timezone
	const currentDateTimeToUtc = convertDateTimeToUtc(currentDateTime, timezone);
	return currentDateTimeToUtc;
};

/**
 * Convert UTC datetime to display date format with proper timezone handling
 * Combines both timezone conversion and date formatting in one step to prevent inconsistencies
 * @param utcDateTime - The UTC datetime string to convert
 * @param timezone - The target timezone
 * @returns Formatted date string (e.g. 'Tue, Oct 13')
 */
export const formatEventDisplayDate = (
	utcDateTime: string,
	timezone: string,
): string => {
	// First convert UTC to target timezone
	const localDateTime = convertDateTimeFromUtcToTimezone(
		utcDateTime,
		timezone,
		DATE_TIME_ISO8601_FORMAT,
	);

	// Then format with strict timezone
	return convertDateTimeWithStrictTimezone(localDateTime, timezone, `ddd, ${EVENT_DISPLAY_DATE_FORMAT}`);
};

/**
 * Convert UTC datetime to display time format with proper timezone handling
 * Combines both timezone conversion and time formatting in one step to prevent inconsistencies
 * @param utcDateTime - The UTC datetime string to convert
 * @param timezone - The target timezone
 * @param format - Optional format string (defaults to TIME_12_HOUR_FORMAT)
 */
export const formatEventDisplayTime = (
	utcDateTime: string,
	timezone: string,
	format: string = TIME_12_HOUR_FORMAT,
): string => {
	// First convert UTC to target timezone
	const localDateTime = convertDateTimeFromUtcToTimezone(
		utcDateTime,
		timezone,
		DATE_TIME_ISO8601_FORMAT,
	);

	// Then format with strict timezone
	return convertDateTimeWithStrictTimezone(localDateTime, timezone, format);
};

export const formatEventCardDisplayDateRange = (
	startUtcDateTime: string,
	endUtcDateTime: string,
	timezone: string,
): string => {
	if (!startUtcDateTime || !timezone) return '';

	const startLocalIso = convertDateTimeFromUtcToTimezone(
		startUtcDateTime,
		timezone,
		DATE_TIME_ISO8601_FORMAT,
	);
	const startLabel = convertDateTimeWithStrictTimezone(startLocalIso, timezone, 'ddd, MMM D');

	if (!endUtcDateTime) return startLabel;

	const endLocalIso = convertDateTimeFromUtcToTimezone(
		endUtcDateTime,
		timezone,
		DATE_TIME_ISO8601_FORMAT,
	);

	const isSameDay = dayjs(startLocalIso).isSame(endLocalIso, 'date');
	if (isSameDay) return startLabel;

	const endLabel = convertDateTimeWithStrictTimezone(endLocalIso, timezone, 'ddd, MMM D');
	return `${startLabel} - ${endLabel}`;
};

export const formatEventCardDisplayTimeRange = (
	startUtcDateTime: string,
	endUtcDateTime: string,
	timezone: string,
): string => {
	if (!startUtcDateTime || !timezone) return '';

	if (!endUtcDateTime) {
		return formatEventDisplayTime(startUtcDateTime, timezone);
	}

	const startLocalIso = convertDateTimeFromUtcToTimezone(
		startUtcDateTime,
		timezone,
		DATE_TIME_ISO8601_FORMAT,
	);
	const endLocalIso = convertDateTimeFromUtcToTimezone(
		endUtcDateTime,
		timezone,
		DATE_TIME_ISO8601_FORMAT,
	);

	const isSameDay = dayjs(startLocalIso).isSame(endLocalIso, 'date');
	return isSameDay
		? formatEventDisplayTime(startUtcDateTime, timezone)
		: formatEventDisplayTime(endUtcDateTime, timezone);
};

export const getWeekOfMonth = (dateTime: string | Date): WeekOfMonth => {
	const firstDayOfMonth = dayjs(dateTime).set('date', 1).format(DATE_PICKER_FORMAT);
	const lastDayOfMonth = dayjs(dateTime).endOf('month').format(DATE_PICKER_FORMAT);
	const modifiedFirstDayOfMonth = new Date(firstDayOfMonth);
	const modifiedLastDayOfMonth = new Date(lastDayOfMonth);
	const targetDate = new Date(dateTime);

	// Compare first day of month with Target date
	// `getDay` return 0-6 start from Sun = 0 to Sat = 6
	// `getDate` return total date of month e.g. 1-31
	const weekOfTargetDate = modifiedFirstDayOfMonth.getDay() + targetDate.getDate();
	// Compare first day of month with last day of month
	const totalDayOfMonth = modifiedFirstDayOfMonth.getDay() + modifiedLastDayOfMonth.getDate();

	const weekOfMonth = Math.ceil(weekOfTargetDate / TOTAL_WEEKDAY);
	const totalWeekOfMonth = Math.ceil(totalDayOfMonth / TOTAL_WEEKDAY);
	const isLastWeek = weekOfMonth === totalWeekOfMonth;

	return {
		weekOfMonth,
		isLastWeek,
	};
};

export const getDayOfWeeks = (dateTime: string | Date, timezone: string) => {
	const date = convertDateTimeWithStrictTimezone(dateTime, timezone, DATE_PICKER_FORMAT);
	const nameOfDayOfWeek = dayjs(date).format(NAME_OF_THE_DAY_OF_THE_WEEK_FORMAT);
	const weekOfMonthObj = getWeekOfMonth(date);
	// E.g. `last Thursday`
	if (weekOfMonthObj.isLastWeek) {
		return `last ${nameOfDayOfWeek}`;
	}

	// E.g. `first Thursday`
	const weekOfMonthName = mappedWeekOfMonth[weekOfMonthObj.weekOfMonth];
	return `${weekOfMonthName} ${nameOfDayOfWeek}`;
};

/**
 * Formats two dates into a string like "May 29th - May 31st".
 * @param {string} startDate - First date in ISO 8601 format (e.g., "2025-05-29T00:00:00Z").
 * @param {string} endDate - Second date in ISO 8601 format (e.g., "2025-05-31T00:00:00Z").
 * @returns {string} Formatted date range string.
 */
export const formatDateRange = (startDate: string, endDate: string) => {
	// Helper function to format a single date with ordinal suffix
	const formatWithOrdinal = (date: dayjs.Dayjs) => {
		const day = date.date();
		let suffix = 'th';
		if (day === 1 || day === 21 || day === 31) suffix = 'st';
		else if (day === 2 || day === 22) suffix = 'nd';
		else if (day === 3 || day === 23) suffix = 'rd';
		return `${date.format('MMMM')} ${day}${suffix}`;
	};

	// Parse the input dates
	const start = dayjs.utc(startDate);
	const end = dayjs.utc(endDate);

	// Format each date and combine into the final string
	return `${formatWithOrdinal(start)} - ${formatWithOrdinal(end)}`;
};

/**
 * Guesses the user's timezone based on their browser settings.
 * @returns {string} The user's timezone (e.g., "America/New_York").
 */
export const getTimezone = (): string => {
	return dayjs.tz.guess();
};
